/* eslint-disable max-lines */
/**
 * Strings do Portal do Cliente (E50) — namespace `portal.*` anexado ao ptBR.
 *
 * `max-lines` desligado como no `pt-BR.ts`: o limite existe para módulo com
 * comportamento, e catálogo de strings cresce com a superfície da UI. Quebrá-lo
 * por tamanho espalharia a busca por uma chave em vários arquivos sem separar
 * responsabilidade nenhuma.
 *
 * REGRAS deste namespace:
 *   - `portal.productName` é a ÚNICA fonte da marca "SignCheck" em UI. A marca
 *     só pode aparecer em telas sob components/Portal/, pages/portal/ e neste
 *     arquivo (docs/domain/naming-conventions.md). A área do perito segue com
 *     `ptBR.productName` neutro.
 *   - Vocabulário oficial do PRD (docs/prd/SignCheck_V2_PRD.md §3, decisão 1
 *     validada com o cliente): "Exame primário" — nunca "pré-análise" — e
 *     "laudo certificado"/"certificação" para o serviço acessório. Desde a
 *     E162.F2.T06 (decisão do owner, 22/10) o menu diz "Enviar documentos" e
 *     "Laudos"; "certificação" segue no texto corrido.
 *   - Valem as mesmas regras do pt-BR.ts: nenhum termo técnico interno,
 *     nenhuma string de UI hardcoded em componente.
 */
export const ptBRPortal = {
  productName: 'SignCheck',
  brandKicker: 'Portal do cliente',

  nav: {
    sectionCarteira: 'Carteira',
    sectionGestao: 'Gestão',
    visaoGeral: 'Visão geral',
    casos: 'Casos',
    // E162.F2.T06 (QA-17, decisão do owner de 22/10): o menu do HTML de 13/10 — a ação é enviar
    // os documentos e o que se acompanha são os laudos; "certificação" segue no texto corrido.
    novoCaso: 'Enviar documentos',
    faturamento: 'Faturamento',
    certificacoes: 'Laudos',
    avisos: 'Avisos',
    usuarios: 'Usuários',
    escritorios: 'Escritórios',
    trilha: 'Trilha de auditoria',
    emBreve: 'Em breve',
    openMenu: 'Abrir menu de navegação',
    closeMenu: 'Fechar menu de navegação',
  },

  shell: {
    userFallback: 'Conta do portal',
    // E162.F2.T05 (QA-23): o banco em cujo contexto a sessão opera, sempre à vista.
    bancoAtivo: 'Banco',
  },

  routes: {
    visaoGeral: {
      title: 'Visão geral',
      description:
        'Acompanhe em um só lugar os casos do mês, os exames primários entregues e as certificações requeridas da sua carteira.',
      emptyHint: 'Os indicadores do mês e o movimento recente serão liberados nesta área em breve.',
    },
    faturamento: {
      title: 'Faturamento',
      description: 'Consulte a medição do mês e o extrato de eventos de serviço concluído.',
      emptyHint: 'Nenhum serviço concluído no período selecionado.',
    },
    casos: {
      title: 'Casos',
      description:
        'Acompanhe aqui os casos do seu escritório: cada processo vira um caso, e cada CCB questionada recebe um exame primário próprio, com resultado por contrato.',
      emptyHint: 'A carteira de casos será liberada nesta área em breve.',
    },
    novoCaso: {
      title: 'Enviar documentos',
      description:
        'O cadastro de casos por processo judicial — declarando uma a uma as CCBs questionadas — será liberado nesta área em breve.',
      emptyHint: 'Enquanto isso, seu contato na equipe de perícia segue recebendo os casos.',
    },
    usuarios: {
      title: 'Usuários',
      description: 'A gestão dos usuários do seu escritório será liberada nesta área em breve.',
      emptyHint: 'Convites e papéis de acesso serão administrados aqui.',
    },
    certificacoes: {
      title: 'Laudos',
      description: 'As entregas de cada caso, por versão.',
      emptyHint: 'As entregas liberadas aparecerão aqui, com a versão.',
    },
    avisos: {
      title: 'Avisos',
      description: 'O que aconteceu nos seus casos, do mais recente para o mais antigo.',
      emptyHint: 'Os avisos dos seus casos aparecerão aqui.',
    },
  },

  // Carteira de casos do escritório-cliente (E50.F4 — versão mínima; o
  // resultado por CCB e a visão do banco chegam em E50.F6/F7).
  // E165.F2.T03 (HTML 6.8, GUS-139): Casos é a mesma tela nas duas visões — busca, filtro pela
  // situação da régua e a tabela do portal.
  casosLista: {
    title: 'Casos',
    novoCaso: 'Enviar documentos',
    busca: 'Buscar caso, CCB, titular, processo ou pasta',
    buscaPlaceholder: 'Digite uma referência',
    filtro: 'Situação',
    filtroTodas: 'Todas',
    contagemUm: '1 caso',
    contagem: '{n} casos',
    limpar: 'Limpar busca e filtro',
    erroCarregar: 'Não foi possível carregar os casos. Recarregue a página.',
    tentarDeNovo: 'Tentar de novo',
    carregando: 'Carregando os casos',
  },

  // Owner, 03/11: a tela Casos do portal por pasta — uma linha por pasta, que abre na própria lista
  // com os casos e as CCBs dela (a tabela de sempre).
  pastasDoPortal: {
    colPasta: 'Pasta',
    colProcesso: 'Processo',
    colTitular: 'Titular',
    colCasos: 'Casos e CCBs',
    colSituacao: 'Situação',
    semPasta: 'Sem pasta informada',
    semProcesso: 'Não informado',
    semTitular: 'Não informado',
    titularesMais: '{titular} e mais {n}',
    casoUm: '1 caso',
    casos: '{n} casos',
    ccbUma: '1 CCB',
    ccbs: '{n} CCBs',
    pastaUma: '1 pasta',
    pastas: '{n} pastas',
    situacaoNaPasta: '{situacao}: {n}',
    casosDaPasta: 'Casos da pasta {pasta}',
  },

  // E165.F2.T03 (HTML 6.8): a tabela do portal, a mesma em Visão geral, Casos e Laudos, nas duas
  // visões. O banco lê também o escritório de cada caso, numa linha embaixo do código.
  tabelaDeCasos: {
    colCaso: 'Caso / CCB',
    colProcesso: 'Processo / Pasta',
    colExame: 'Exame',
    colSituacao: 'Situação',
    colProximaAcao: 'Próxima ação',
    colVersao: 'Versão',
    colAcesso: 'Acesso',
    ccb: 'CCB {ccbs}',
    semProcesso: 'Não informado',
    semPasta: 'Pasta não informada',
    pasta: 'Pasta {pasta}',
    escritorio: 'Escritório: {nome}',
    abrirCaso: 'Abrir o caso {codigo}',
    aindaNaoLiberada: 'Ainda não liberada',
    vazioDoFiltro: 'Nenhum caso encontrado para este filtro.',
    vazio: 'Nenhum caso neste acesso.',
    exame: {
      grafotecnica: 'Contrato Não Digital',
      eletronica: 'Contrato Digital',
    },
  },

  // E165.F2.T02 (HTML 6.8, GUS-140): a Visão geral é uma tela só para as duas visões — o universo
  // e a hora reais da leitura, os três contadores da régua, os casos recentes e os atalhos.
  visaoGeral68: {
    title: 'Visão geral',
    universo: 'Todos os casos · atualizado em {data}',
    emAvaliacao: 'Casos em avaliação',
    emAvaliacaoTexto: 'Aguardando a equipe',
    entregas: 'Entregas disponíveis',
    entregasTexto: 'Exame e relatório de risco',
    acaoNecessaria: 'Ação necessária',
    acaoNecessariaTexto: 'Complementos solicitados',
    recentes: 'Casos recentes',
    verTodos: 'Ver todos os casos',
    verAvisos: 'Ver avisos',
  },

  // E165.F2 (HTML 6.8): o rótulo de cada visão acima do título das telas do portal.
  visao: {
    banco: 'Banco',
    escritorio: 'Escritório',
  },

  // E165.F2.T01 (ADR-0114 §6): a régua única da situação do cliente, a mesma na Visão geral, em
  // Casos, em Laudos, nos avisos e no caso. O servidor decide; a tela só traduz.
  situacaoDoCliente: {
    situacao: {
      // E193.F1 (GUS-259): o caso digital sem o kit completo, parado até o complemento.
      aguardando_arquivos: 'Aguardando arquivos',
      aguardando_documentos: 'Aguardando documentos',
      em_avaliacao: 'Em avaliação',
      entrega_disponivel: 'Entrega disponível',
      // E224.F3 (ADR-0151 §5): a equipe cancelou o caso; a lista diz o motivo.
      cancelado: 'Cancelado',
    },
    proximaAcao: {
      enviar_complemento: 'Complemento solicitado',
      acompanhar_caso: 'A equipe segue com a avaliação',
      ver_relatorios: 'Exame disponível',
      verRelatoriosComRisco: 'Exame e relatório de risco disponíveis',
      cancelado: 'Cancelado pela equipe: {motivo}',
    },
    acao: {
      enviar_complemento: 'Enviar complemento',
      acompanhar_caso: 'Acompanhar caso',
      ver_relatorios: 'Ver relatórios',
    },
    versao: 'Versão {n}',
  },

  // E165.F2.T04 (HTML 6.8, GUS-109): Laudos lista as entregas por caso e versão, na tabela do
  // portal. O painel das certificações requeridas saiu (a requisição segue no servidor).
  laudos68: {
    title: 'Laudos',
    entregas: 'Entregas por caso e versão',
  },

  // E66.F3 — avisos por evento (ADR-0071). O servidor manda o TIPO do acontecimento e o fato; a
  // frase nasce aqui, na leitura.
  // E165.F2.T06 (HTML 6.8, GUS-150): "Atualizações dos casos" — a referência do caso, a situação em
  // box, o fato, o texto real, a data e hora e a ação contextual. A entrega só é avisada com o
  // conjunto liberado; o pedido já atendido abre a situação de agora.
  avisos: {
    title: 'Avisos',
    cartao: 'Atualizações dos casos',
    referenciaComCcb: '{caso} · CCB {ccb}',
    contadorNaoVistos: '{n} sem ler',
    contadorTudoLido: 'Tudo lido',
    titulo: {
      documento_solicitado: 'Documentos solicitados',
      exame_certificado: 'Entrega disponível',
      mensagem_da_equipe: 'Mensagem da equipe',
    },
    tituloIndefinido: 'Atualização do caso',
    textoPedido: 'Documento solicitado pela equipe: {material}.',
    textoPedidoSemMaterial: 'A equipe solicitou um documento.',
    textoEntrega: 'Exame disponível em Relatórios.',
    textoEntregaComRisco: 'Exame e relatório de risco da mesma versão disponíveis em Relatórios.',
    textoMensagem: 'Registrado na conversa: {tipo}.',
    textoIndefinido: 'Houve uma atualização neste caso.',
    abrirConversa: 'Abrir conversa',
    abrirCaso: 'Abrir o caso',
    marcarVisto: 'Marcar como lido',
    marcado: 'Lido',
    marcarErro: 'Não foi possível marcar como lido. Tente de novo.',
    vazio: 'Nenhum aviso neste acesso.',
    naoLidosNoMenu: '{n} avisos sem ler',
    carregando: 'Carregando os avisos',
    erroCarregar: 'Não foi possível carregar os avisos. Recarregue a página.',
  },

  // T4 — medição e extrato do Banco Gestor (E51.F3).
  faturamento: {
    kicker: 'Medição por evento',
    title: 'Faturamento',
    subtitle: 'Confira os serviços concluídos no mês e a faixa aplicada a cada CCB.',
    mesLabel: 'Mês de referência',
    totalMes: 'Total medido no mês',
    totalHint:
      'A soma considera cada evento contra a tabela vigente na data da conclusão do serviço.',
    kpiExames: 'Exames primários no mês',
    kpiExamesFoot: 'Serviços concluídos',
    kpiCertificacoes: 'Certificações requeridas',
    kpiCertificacoesFoot: 'Solicitações registradas no período',
    kpiTicket: 'Ticket médio do exame',
    kpiTicketFoot: 'Por faixa de volume',
    kpiTicketFootVazio: 'Nenhum exame liquidado neste mês',
    kpiAdicional: 'Adicional médio de certificação',
    kpiAdicionalFoot: 'Complemento até o valor agregado',
    kpiAdicionalFootVazio: 'Nenhuma certificação liquidada neste mês',
    faixaNumero: 'Faixa {n}',
    servicoExame: 'Exame primário',
    servicoCertificacao: 'Certificação',
    eventoExame: 'Exame entregue',
    eventoLaudo: 'Laudo assinado',
    extratoTitulo: 'Extrato do mês',
    extratoHint: 'Cada linha nasce de um evento de serviço concluído e mantém a faixa aplicada.',
    extratoVazioTitulo: 'Nenhum serviço concluído neste mês',
    extratoVazioTexto:
      'Quando um exame for entregue ou um laudo for assinado, o evento aparecerá aqui.',
    exportarCsv: 'Exportar CSV',
    exportarEventos: 'Exportar log de eventos',
    exportando: 'Preparando arquivo…',
    erroCarregar: 'Não foi possível carregar a medição. Tente novamente.',
    notaAuditoria:
      'A contagem nasce de eventos imutáveis de serviço concluído. O extrato pode ser reconstituído a partir desses registros e da tabela vigente na data de cada evento.',
    colCaso: 'Caso',
    colCcb: 'CCB',
    colEscritorio: 'Escritório',
    colServico: 'Serviço',
    colFaixa: 'Faixa',
    colVolume: 'Volume no mês',
    colEvento: 'Evento de medição',
    colValor: 'Valor',
    colLiquidado: 'Valor liquidado',
    ccbNaoInformada: 'Não informada',
  },

  // E50.F7 — gestão de usuários do portal (FR-USR, mockup usuarios.html).
  // Rótulos por ambiente (portal-ia §4.6): escritório → Gestor/Analista;
  // banco → Gestor/Usuário comum. Criação direta com senha provisória.
  usuarios: {
    eyebrow: 'PESSOAS COM ACESSO',
    title: 'Usuários',
    subtitle:
      'Gerencie o acesso da sua equipe. Cada usuário opera somente no ambiente do escritório.',
    subtitleBanco: 'Gerencie o acesso da equipe. Cada usuário opera somente no ambiente do banco.',
    adicionar: 'Adicionar usuário',
    colUsuario: 'Usuário',
    colPapel: 'Papel',
    colStatus: 'Status',
    papelGestor: 'Gestor',
    papelAnalista: 'Analista',
    papelUsuarioComum: 'Usuário comum',
    statusAtivo: 'Ativo',
    voce: 'Você',
    tornarGestor: 'Tornar Gestor',
    tornarAnalista: 'Tornar Analista',
    tornarUsuarioComum: 'Tornar Usuário comum',
    confirmarPapelTitulo: 'Alterar papel',
    confirmarPapelTexto: '{usuario} passa a operar como {papel}.',
    desativar: 'Desativar',
    confirmarDesativarTitulo: 'Desativar usuário',
    confirmarDesativarTexto: 'O acesso de {usuario} é encerrado imediatamente.',
    confirmar: 'Confirmar',
    cancelar: 'Cancelar',
    dialogTitulo: 'Adicionar usuário',
    dialogHint:
      'Informe os dados de acesso. A senha é provisória: a pessoa troca no próprio perfil depois do primeiro acesso.',
    dialogNomeLabel: 'Nome (opcional)',
    dialogEmailLabel: 'E-mail',
    dialogSenhaLabel: 'Senha provisória',
    dialogPapelLabel: 'Papel',
    dialogCriar: 'Adicionar',
    dialogCriando: 'Adicionando…',
    dialogEmailInvalido: 'Informe um e-mail válido.',
    dialogSenhaCurta: 'A senha precisa de ao menos 8 caracteres, uma letra maiúscula e um símbolo.',
    erroEmailEmUso: 'Este e-mail já está cadastrado.',
    erroGenerico: 'Não foi possível concluir a ação. Tente novamente.',
    erroCarregar: 'Não foi possível carregar os usuários. Recarregue a página.',
  },

  // E61.F2 — escritórios que o BANCO administra. Só o gestor do banco chega
  // aqui, e só alcança escritório que o próprio banco abriu (ADR-0064 §8).
  escritorios: {
    eyebrow: 'ESTRUTURA DO BANCO',
    title: 'Escritórios',
    subtitle:
      'Abra escritórios vinculados ao banco — o backoffice ou o de um cliente — e administre quem tem acesso a cada um.',
    cadastrar: 'Cadastrar escritório',
    colEscritorio: 'Escritório',
    colCnpj: 'CNPJ',
    colPessoas: 'Pessoas com acesso',
    colAbertoEm: 'Aberto em',
    semCnpj: '—',
    vazioTitulo: 'Nenhum escritório cadastrado ainda',
    vazioTexto:
      'Cadastre o primeiro escritório do banco junto com o gestor que vai operá-lo. Ele passa a abrir casos como qualquer escritório da carteira.',
    erroCarregar: 'Não foi possível carregar os escritórios. Recarregue a página.',
    novoTitulo: 'Cadastrar escritório',
    novoHint:
      'O escritório nasce com o primeiro gestor. A senha é provisória: a pessoa troca no próprio perfil depois do primeiro acesso.',
    novoNomeLabel: 'Nome do escritório',
    novoCnpjLabel: 'CNPJ (opcional)',
    novoGestorNomeLabel: 'Nome do gestor (opcional)',
    novoGestorEmailLabel: 'E-mail do gestor',
    novoGestorSenhaLabel: 'Senha provisória',
    novoCriar: 'Cadastrar',
    novoCriando: 'Cadastrando…',
    novoNomeObrigatorio: 'Informe o nome do escritório.',
    novoCnpjInvalido: 'O CNPJ precisa ter 14 dígitos.',
    voltar: 'Voltar para escritórios',
    naoEncontradoTitulo: 'Escritório não encontrado',
    naoEncontradoTexto: 'Este escritório não está sob administração do banco.',
    pessoasTitulo: 'Pessoas com acesso',
    pessoasSubtitulo: 'Quem opera este escritório e com qual papel.',
    adicionarPessoa: 'Adicionar pessoa',
    // E69.F3 (ADR-0072 §2.8): o banco encerra o escritório que abriu.
    encerrar: 'Encerrar escritório',
    encerrarConfirmacao:
      'Encerrar {nome}? Todas as pessoas dele perdem o acesso agora. Os casos e o histórico ficam guardados, e o escritório sai da sua lista.',
    encerrarConfirmar: 'Confirmar encerramento',
    encerrarCancelar: 'Cancelar',
    encerrarErro: 'Não foi possível encerrar o escritório. Tente novamente.',
    colPessoa: 'Pessoa',
    colPapel: 'Papel',
    colStatus: 'Status',
    papelGestor: 'Gestor',
    papelAnalista: 'Analista',
    papelRevisor: 'Revisor',
    statusAtivo: 'Ativo',
    statusInativo: 'Sem acesso',
    alterarPapel: 'Alterar papel',
    desativar: 'Desativar',
    reativar: 'Reativar',
    confirmarPapelTitulo: 'Alterar papel',
    confirmarPapelHint: 'Escolha o papel de {pessoa} neste escritório.',
    confirmarDesativarTitulo: 'Desativar acesso',
    confirmarDesativarTexto: 'O acesso de {pessoa} é encerrado imediatamente.',
    confirmarReativarTitulo: 'Reativar acesso',
    confirmarReativarTexto: '{pessoa} volta a operar neste escritório.',
    confirmar: 'Confirmar',
    cancelar: 'Cancelar',
    pessoaTitulo: 'Adicionar pessoa',
    pessoaHint: 'A senha é provisória: a pessoa troca no próprio perfil depois do primeiro acesso.',
    pessoaNomeLabel: 'Nome (opcional)',
    pessoaEmailLabel: 'E-mail',
    pessoaSenhaLabel: 'Senha provisória',
    pessoaPapelLabel: 'Papel',
    pessoaCriar: 'Adicionar',
    pessoaCriando: 'Adicionando…',
    emailInvalido: 'Informe um e-mail válido.',
    senhaCurta: 'A senha precisa de ao menos 8 caracteres, uma letra maiúscula e um símbolo.',
    erroEmailEmUso: 'Este e-mail já está cadastrado.',
    erroUltimoGestor: 'O escritório precisa de pelo menos um Gestor ativo.',
    erroGenerico: 'Não foi possível concluir a ação. Tente novamente.',
    erroCarregarPessoas: 'Não foi possível carregar as pessoas. Recarregue a página.',
  },

  // E61.F4 — a trilha do PRÓPRIO banco. A moldura é daqui; o vocabulário dos
  // registros (colunas, ações, estados) vem de `ptBR.trilha`, o mesmo que o
  // console da plataforma usa.
  trilha: {
    eyebrow: 'REGISTRO DE ATIVIDADE',
    title: 'Trilha de auditoria',
    subtitle:
      'Quem fez o quê, quando e sobre quem — no banco e nos escritórios que ele administra.',
    desdeLabel: 'De',
    ateLabel: 'Até',
    autorLabel: 'Quem fez',
    limpar: 'Limpar filtros',
    anterior: 'Anterior',
    proxima: 'Próxima',
    resumo: '{inicio}–{fim} de {total}',
    erroPeriodo: 'Confira as datas do período e tente de novo.',
    erroSemAcesso: 'Você não tem acesso a este registro.',
    erroGenerico: 'Não foi possível carregar o registro. Recarregue a página.',
  },

  // Tutorial de primeiro uso do cadastro (FR-INT-07): questionado × comparativo.
  tutorial: {
    titulo: 'Antes do primeiro caso',
    intro: 'Dois tipos de documento entram no cadastro — e não podem ser misturados:',
    questionadoTitulo: 'CCB questionada',
    questionadoTexto:
      'O contrato com a assinatura contestada. Cada CCB ocupa um slot próprio e gera um exame primário e um laudo próprios dentro do caso.',
    comparativoTitulo: 'Documento comparativo',
    comparativoTexto:
      'Traz assinaturas verdadeiras do autor — RG, procurações, contratos anteriores. Os comparativos são do caso e valem para todas as CCBs.',
    entendi: 'Entendi, começar',
  },

  // Wizard de novo caso (E50.F4 — T1 do PRD, passos Dados → Contratos →
  // Documentos → Revisão).
  wizard: {
    kicker: 'Enviar documentos',
    // E126.F2: o primeiro passo é a abertura por serviço (a tela Dados saiu).
    stepDados: 'Serviço',
    stepContratos: 'Contratos',
    stepDocumentos: 'Documentos',
    stepRevisao: 'Revisão',
    voltar: 'Voltar',
    dados: {
      title: 'Dados do caso',
      subtitle: 'Um processo corresponde a um caso — informe o processo e declare o objeto.',
      tituloLabel: 'Referência do caso',
      tituloHint: 'Como o seu escritório identifica esta pasta.',
      tituloErro: 'Informe a referência do caso.',
      processoLabel: 'Número do processo',
      processoHint: 'Formato CNJ. O processo define o caso.',
      processoErro: 'Informe o número do processo no formato CNJ (0000000-00.0000.0.00.0000).',
      processoConflito:
        'Já existe um caso para este processo. Abra o caso existente na sua carteira.',
      partesLabel: 'Partes (opcional)',
      ccbsLabel: 'Quantas CCBs são objeto da contestação?',
      ccbsHint: 'A quantidade declarada define os slots de contrato do próximo passo.',
      ccbsErro: 'Declare de 1 a 20 CCBs.',
      // E74.F1 (ADR-0075 §1) — modalidade do caso, escolhida aqui e imutável depois.
      modalidadeLabel: 'Modalidade da análise',
      modalidadeHint:
        'Grafotécnica compara o traço da assinatura; eletrônica verifica o protocolo da assinatura eletrônica da CCB.',
      modalidadeGrafotecnica: 'Análise grafotécnica',
      modalidadeEletronica: 'Análise de assinatura eletrônica',
      criarErro: 'Não foi possível criar o caso. Verifique os campos e tente de novo.',
      criando: 'Criando o caso…',
      avancar: 'Avançar para Contratos',
    },
    contratos: {
      title: 'Contratos questionados',
      subtitle:
        'Uma CCB por slot — cada CCB questionada gera um exame primário e um laudo próprios dentro do caso.',
      processoCard: 'Processo (define o caso)',
      ccbsCard: 'CCBs declaradas como objeto',
      alterar: 'Alterar',
      slotTitle: 'CCB {{n}} de {{total}}',
      numeroLabel: 'Nº da CCB',
      numeroPlaceholder: 'Nº a informar',
      numeroDuplicado: 'Este número já está em outro slot.',
      informeNumero: 'Informe o número da CCB para liberar o envio do arquivo.',
      dropTitle: 'Arraste a CCB aqui ou clique para selecionar',
      dropHint: 'Um arquivo por CCB — PDF, DOCX, JPG ou PNG',
      recebido: 'Recebido',
      remover: 'Remover',
      enviando: 'Enviando…',
      slotNote: 'Somente um arquivo de CCB por slot — um segundo arquivo aqui será recusado.',
      umPorSlot: 'Somente um arquivo por slot — envie a outra CCB em um slot próprio.',
      duplicadoNoCaso: 'Este número de CCB já está declarado neste caso.',
      uploadErro: 'Não foi possível receber o arquivo. Tente de novo.',
      tamanhoExcedido: 'Arquivo acima do limite de 50 MB.',
      comoTrataTitulo: 'Como o sistema trata:',
      comoTrataTexto:
        '1 processo = 1 caso. Dentro do caso, cada CCB questionada gera um exame primário e um laudo próprios — datas e assinaturas mudam de contrato para contrato. Os documentos comparativos do próximo passo valem para todas as CCBs.',
      faltamSlots: 'Preencha todos os slots declarados para avançar — ou ajuste a quantidade.',
      avancar: 'Avançar para Documentos',
    },
    documentos: {
      title: 'Documentos comparativos',
      subtitle:
        'Assinaturas verdadeiras do autor para comparação. Os comparativos são do caso e valem para todas as CCBs.',
      dropTitle: 'Arraste os comparativos aqui ou clique para selecionar',
      dropHint: 'RG, procurações, contratos anteriores — PDF, DOCX, JPG ou PNG',
      vazio: 'Nenhum comparativo enviado ainda.',
      remover: 'Remover',
      enviando: 'Enviando…',
      uploadErro: 'Não foi possível receber o arquivo. Tente de novo.',
      // E133.F2 (GUS-106): o lote segue, e a tela diz quais arquivos não entraram.
      tamanhoExcedido: 'Arquivo acima do limite de 50 MB.',
      avancar: 'Avançar para Revisão',
    },
    // E74.F1 — mesmo passo, texto da modalidade eletrônica: não há comparação de
    // traço, então "comparativo" não se aplica; o que entra aqui são os
    // documentos do protocolo (certificado de conclusão, comprovantes) que
    // acompanham a CCB.
    //
    // E88.F3 (GUS-30 RF07/CA06): "peça" é vocabulário processual do perito; o
    // cliente do portal lê "documento". A troca é só nas telas DELE — o
    // vocabulário do perito fica como está, e mudar os dois de uma vez
    // confundiria as duas audiências.
    documentosEletronica: {
      title: 'Documentos do protocolo',
      subtitle:
        'Anexe o certificado de conclusão da plataforma de assinatura, comprovantes e demais documentos do protocolo. Comparativos de assinatura não se aplicam à modalidade eletrônica.',
      dropTitle: 'Arraste os documentos do protocolo aqui ou clique para selecionar',
      dropHint:
        'Certificado de conclusão, comprovantes — PDF, DOCX, JPG ou PNG; o manifesto de evidências (JSON) e o carimbo do tempo (TST) do banco',
      vazio: 'Nenhum documento enviado ainda.',
      remover: 'Remover',
      enviando: 'Enviando…',
      uploadErro: 'Não foi possível receber o arquivo. Tente de novo.',
      // E133.F2 (GUS-106): o lote segue, e a tela diz quais arquivos não entraram.
      tamanhoExcedido: 'Arquivo acima do limite de 50 MB.',
      avancar: 'Avançar para Revisão',
    },
    revisao: {
      title: 'Revisão e submissão',
      subtitle:
        'Confira o objeto declarado e confirme as assinaturas encontradas em cada documento antes de submeter.',
      resumoProcesso: 'Processo',
      resumoCcbs: 'CCBs declaradas',
      resumoComparativos: 'Comparativos',
      confirmadas: 'Assinaturas confirmadas',
      pendente: 'Confirmação pendente',
      conferir: 'Conferir assinaturas',
      fecharConferencia: 'Fechar conferência',
      extracaoIntro:
        'Encontramos {{count}} assinatura(s) neste documento. Confirme ou ajuste antes de submeter.',
      extracaoIntroVazia:
        'Nenhuma assinatura foi localizada automaticamente neste documento. Marque a região da assinatura para prosseguir.',
      aguardandoExtracao: 'Preparando o documento…',
      exameInfo:
        'Ao submeter, cada CCB declarada segue para o exame primário. O resultado aparece na sua carteira, por contrato.',
      semComparativoTitulo: 'Submeter sem comparativos?',
      semComparativoTexto:
        'Sem documentos comparativos, o exame primário tende a resultar inconclusivo. Você pode voltar e anexar comparativos, ou submeter assim mesmo.',
      submeterMesmoAssim: 'Submeter assim mesmo',
      cancelar: 'Cancelar',
      submeter: 'Submeter caso',
      submetendo: 'Submetendo o caso…',
      submetido: 'Caso submetido — o exame primário está em andamento.',
      exameFalha:
        'A submissão não foi concluída — o exame primário não foi iniciado para: {{lista}}.',
      exameFalhaParcial:
        'Os demais contratos já seguiram para o exame primário e não serão reenviados.',
      exameFalhaAjuda: 'Seus documentos e confirmações estão guardados. Tente novamente.',
      falhaCcbItem: 'CCB {{numero}}',
      tentarNovamente: 'Tentar novamente',
      // E74.F1 — modalidade eletrônica: sem conferência de assinaturas nem
      // exame automático; a CCB segue para a transcrição do protocolo.
      eletronicaSubtitle: 'Confira as CCBs e os documentos do protocolo antes de submeter.',
      eletronicaResumoPecas: 'Documentos do protocolo',
      eletronicaRecebido: 'Recebido',
      eletronicaExameInfo:
        'Ao submeter, cada CCB declarada segue para a verificação do protocolo de assinatura eletrônica. O resultado aparece na sua carteira, por contrato.',
      eletronicaSubmetido: 'Caso submetido — a verificação do protocolo será feita por contrato.',
    },
  },

  // T2 — resultado por CCB + requisição de certificação (E50.F6; PRD §5 T2,
  // régua visual docs/design/portal/caso-resultado.html).
  resultado: {
    kicker: 'Caso',
    breadcrumbCasos: 'Casos',
    subTemplate: '{{ccbs}} · aberto em {{data}}',
    umaCcb: '1 CCB neste caso',
    variasCcbs: '{{n}} CCBs neste caso',
    bannerEntregue: 'Exame primário entregue · serviço concluído',
    bannerEmCurso: 'Exame primário em curso',
    // E140.F3 (GUS-143, CA07): a conferência interna da leitura, sem ação do banco.
    bannerConferenciaInterna: 'Exame primário em conferência interna · nenhuma ação do banco',
    pillExaminadas: '{{feitas}} de {{total}} CCBs examinadas',
    cardTitle: 'Resultado por CCB',
    cardHint: 'A certificação é requerida CCB a CCB — a primeira requisição trava a segunda.',
    colCcb: 'CCB',
    colArquivo: 'Arquivo',
    colResultado: 'Resultado do exame primário',
    colCertificacao: 'Certificação',
    requerer: 'Requerer certificação',
    requerendo: 'Requerendo…',
    requeridaPeloEscritorio: 'Requerida pelo escritório',
    requeridaPeloBanco: 'Requerida pelo banco',
    requisicaoSub: '{{data}} · {{autor}} · prazo',
    aguardandoExame: 'Disponível após a entrega do exame.',
    confirmTitle: 'Requerer certificação',
    confirmDescription:
      'A certificação desta CCB será requerida ao corpo pericial, com prazo de 5 dias úteis. A requisição é única por CCB e fica registrada com autor e data.',
    confirmCta: 'Requerer',
    confirmCancel: 'Cancelar',
    conflitoAviso: 'Esta CCB já teve a certificação requerida por {{autor}} ({{ambiente}}).',
    ambienteEscritorio: 'escritório',
    ambienteBanco: 'banco',
    nota: 'O resultado do exame primário é uma triagem técnica, não uma conclusão pericial. A conclusão formal, em razão de verossimilhança, sai apenas no laudo certificado e assinado. A requisição de certificação é única por CCB: feita por um lado, o outro enxerga quem requereu e quando.',
    comparativosTitle: 'Documentos comparativos do caso',
    comparativosHint: 'Valem para todas as CCBs do caso.',
    comparativosVazio: 'Nenhum documento comparativo neste caso.',
    visualizacaoTitle: 'Visualização do exame',
    visualizacaoHint: 'Marcações do exame primário sobre a assinatura questionada.',
    visualizacaoEmExame: 'A visualização fica disponível quando o exame desta CCB for entregue.',
    visualizacaoVazia: 'Sem marcações registradas para esta CCB.',
    // E65.F4 (ADR-0070): o indicativo da triagem aparece SÓ quando o servidor
    // envia a métrica — ambiente escritório; o banco nunca recebe o campo.
    triagemSinteseTitle: 'Síntese da triagem',
    verExame: 'Ver exame',
    ocultarExame: 'Ocultar exame',
    convergencia: 'Convergência',
    divergencia: 'Divergência',
    certificada: 'Laudo certificado',
    certificadaSub: 'Assinado em {{data}} por {{autor}}',
    // E65.F1.T03 — item 11 do E2E do owner: quem assinou vivia só na coluna de
    // certificação, quarta de quatro. Aqui a mesma leitura sobe para o topo do
    // caso, nos dois ambientes.
    assinadoResumo: 'Laudo certificado · assinado em {{data}} por {{autor}}',
    assinadoResumoVarios: '{{n}} laudos certificados · último assinado em {{data}} por {{autor}}',
    baixarLaudo: 'Baixar laudo certificado',
    baixandoLaudo: 'Preparando…',
    erroBaixarLaudo: 'Não foi possível baixar o laudo certificado. Tente novamente.',
    erroEntregaIncompleta:
      'O laudo desta versão é entregue junto com o relatório de risco, que ainda está sendo liberado. Tente de novo em instantes.',
    laudoIndisponivel: 'Arquivo indisponível para download.',
    codigoVerificacaoLabel: 'Código de verificação do arquivo',
    // E65.F6 — item 5 do E2E do owner: o rótulo diz QUAL arquivo desce. Numa
    // lista de vários documentos, três controles chamados "baixar" não dizem
    // nada a quem navega por leitor de tela.
    baixarArquivo: 'Baixar {{arquivo}}',
    erroBaixarArquivo: 'Não foi possível baixar. Tente novamente.',
    // E134.F2 (GUS-159): o arquivo guardado não confere com o recebimento e não é entregue.
    erroOriginalDivergente:
      'O arquivo guardado não confere com o registro do recebimento e não foi entregue. A ocorrência ficou registrada.',
    assinaturaQuestionada: 'assinatura questionada',
    assinaturaComparativa: 'assinatura comparativa',
    erroCarregar: 'Não foi possível carregar o caso. Tente novamente.',
    erroRequerer: 'Não foi possível requerer a certificação. Tente novamente.',
    carregando: 'Carregando o caso…',

    // E80.F3 — um lugar só onde o cliente pega o que é dele. O relatório da
    // análise preliminar nasce aqui (na eletrônica ele não existia em documento
    // nenhum); o laudo continua vindo do bloco da certificação, que já sabe
    // quando ele existe.
    // E91.F2 (GUS-51 RN01/RF03): o bloco se chama pela AÇÃO, o nome formal do
    // documento fica em segundo nível, e a mesma ação sobe ao topo do caso.
    saidas: {
      titulo: 'Baixar exame',
      hint: 'O que este caso já produziu, pronto para baixar.',
      relatorioTitulo: 'Relatório da análise preliminar',
      relatorioSub:
        'Registro formatado da análise preliminar deste caso. Não é laudo pericial e não possui responsável técnico.',
      baixarRelatorio: 'Baixar exame',
      topoIndisponivel: 'Exame disponível quando todos os documentos forem examinados.',
      baixandoRelatorio: 'Preparando…',
      // Indisponível diz POR QUÊ. Botão morto obriga o cliente a adivinhar se
      // o documento não existe ou se o sistema falhou.
      relatorioIndisponivel:
        'Disponível quando o exame de todos os documentos deste caso for entregue.',
      // E154.F2: o documento é o que a equipe emitiu do exame; com o exame
      // entregue e nada emitido ainda, o servidor recusa — a tela diz o que falta.
      relatorioAindaNaoEmitido:
        'O documento deste caso ainda não foi emitido pela equipe. Tente de novo mais tarde.',
      erroBaixarRelatorio: 'Não foi possível baixar o relatório. Tente novamente.',
      // E169.F2 (ADR-0117 §4): em PARTE, o laudo só sai com o risco da mesma versão.
      entregaIncompleta:
        'O laudo desta versão é entregue junto com o relatório de risco, que ainda está sendo liberado. Tente de novo em instantes.',
      laudoTitulo: 'Laudo certificado',
      laudoPendente: 'Disponível quando o laudo certificado for entregue.',
    },
  },
} as const;
